import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import {
  type AgentHubFilters,
  CADENCES,
  CHART_METRICS,
  FILTER_DEFINITIONS,
  SIGNUP_LEVELS,
  SPLITS,
  TABS,
  type Tab,
} from './model';

const VISIBLE_ROWS = 25;

type PageSection = {
  title: string;
  rows: Array<{ label: string; values: Record<string, number | string | null> }>;
  truncated: boolean;
};

export function agentHubPageView(input: {
  ready: boolean;
  filters: AgentHubFilters;
  options: Record<string, Array<string>>;
  period: string;
  comparison: string | null;
  summary: Record<string, number | null> | null;
  sections: Array<PageSection>;
}): PageView {
  const { filters, options } = input;
  const filterSchema: Record<string, PageField> = {
    tab: { type: 'enum', values: TABS.map((tab) => tab.value) },
    from: { type: 'date' },
    to: { type: 'date' },
    refunds: { type: 'enum', values: ['AR', 'BR'] },
    cadence: { type: 'enum', values: CADENCES.map((item) => item.value) },
    chartMetric: { type: 'enum', values: CHART_METRICS.map((item) => item.value) },
    split: { type: 'enum', values: SPLITS.map((item) => item.value) },
    signupLevel: { type: 'enum', values: SIGNUP_LEVELS.map((item) => item.value) },
    ...Object.fromEntries(
      FILTER_DEFINITIONS.map(({ key }) => [key, { type: 'enum', values: [null, ...(options[key] ?? [])] }]),
    ),
  };
  return {
    title: 'Agent Hub Dashboard',
    ready: input.ready,
    dataMode: 'live analytics.sales_with_refunds and analytics.agency_hub_agents',
    filters: {
      tab: filters.tab,
      from: filters.from,
      to: filters.to,
      refunds: filters.refunds,
      cadence: filters.cadence,
      chartMetric: filters.chartMetric,
      split: filters.split,
      signupLevel: filters.signupLevel,
      ...Object.fromEntries(
        FILTER_DEFINITIONS.map(({ key }) => [
          key,
          filters.selections[key].length === 1 ? filters.selections[key][0] : null,
        ]),
      ),
    },
    filterSchema,
    activeSelections: Object.fromEntries(
      FILTER_DEFINITIONS.filter(({ key }) => filters.selections[key].length).map(({ key }) => [
        key,
        filters.selections[key],
      ]),
    ),
    period: input.period,
    comparisonPeriod: input.comparison,
    summary: input.summary,
    visibleSections: input.sections.map((section) => ({
      title: section.title,
      totalRows: section.rows.length,
      rows: section.rows.slice(0, VISIBLE_ROWS).map((row) => ({ label: row.label, ...row.values })),
      truncated: section.truncated || section.rows.length > VISIBLE_ROWS,
    })),
    definitions: {
      source:
        'Orders and Finance: lux-analytics-prod.analytics.sales_with_refunds for LE Agent Hub and CVFR Holidays. Agents: lux-analytics-prod.analytics.agency_hub_agents, agents without an affiliation under Unknown. Queried live; rebuilt daily.',
      dates:
        'Sydney transaction dates for orders, sign-up dates for agents, to yesterday. KPI changes compare with the same number of days just before the window. Active and churned are as of today.',
      measures:
        'AR nets refunds processed in the window. Net margin = margin less agent commission. Agent nett = gross sales less commission; override = 2% of agent nett from each affiliation’s start date. AOV = gross sales before refunds / orders. Orders, agents and agencies are distinct on sale rows. Active = an order in the last 90 days; churned = signed up over 180 days ago and never ordered.',
      missing: 'Agent names are masked in the warehouse, so agents show their ID where the name is hidden.',
      filters:
        'Each filter accepts one value or null for all values here. activeSelections lists every value currently selected. Offer country, product, category and vendor apply to orders only.',
    },
  };
}

/** Applies a validated askRichard edit: one value per filter, or null for every value. */
export function editFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: AgentHubFilters,
): Partial<AgentHubFilters> {
  validatePageFilters(changes, view.filterSchema);
  const next: Partial<AgentHubFilters> = {};
  if ('tab' in changes) {
    next.tab = changes.tab as Tab;
  }
  for (const key of ['from', 'to', 'refunds', 'cadence', 'chartMetric', 'split', 'signupLevel'] as const) {
    if (key in changes) {
      Object.assign(next, { [key]: changes[key] });
    }
  }
  const selections = { ...filters.selections };
  let changed = false;
  for (const { key } of FILTER_DEFINITIONS) {
    if (key in changes) {
      const value = changes[key] as string | null;
      selections[key] = value === null ? [] : [value];
      changed = true;
    }
  }
  if (changed) {
    next.selections = selections;
  }
  return next;
}
